import { useMemo, useState, type ReactNode } from 'react';
import { AnalyticsTrendChart } from '@/components/miniprogramAnalytics/AnalyticsTrendChart';
import { AnalyticsDeltaBadge } from '@/components/miniprogramAnalytics/AnalyticsDeltaBadge';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { inlineErrorClass } from '@/components/ui/inputStyles';
import type { AnalyticsResp } from '@/lib/miniprogramAnalyticsShared';
import {
  METRICS,
  MOBILE_CHART_BAR_AREA_PX,
  MOBILE_TREND_METRIC_KEYS,
  PERIOD_OPTIONS,
  POST_SLOT_METRICS,
  REWARD_AD_SUBMETRICS,
  formatNum,
  metricByKey,
  type MetricDef,
  type MetricKey,
} from '@/lib/miniprogramAnalyticsShared';

export type AnalyticsMpOption = {
  id: number;
  name: string;
  enabled?: boolean;
  is_slave?: boolean;
  master_name?: string | null;
  direct_assigned?: boolean;
};

function MobileKpiTile({
  def,
  todayVal,
  yesterdayVal,
}: {
  def: MetricDef;
  todayVal: number;
  yesterdayVal: number;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-3 shadow-sm">
      <div
        className={cn('pointer-events-none absolute -right-4 -top-4 h-16 w-16 rounded-full bg-gradient-to-br opacity-10', def.accent)}
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[11px] font-semibold text-slate-500">{def.short}</p>
          <p className="mt-0.5 text-2xl font-bold tabular-nums tracking-tight text-slate-900">{formatNum(todayVal)}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            <AnalyticsDeltaBadge today={todayVal} yesterday={yesterdayVal} compact />
            <span className="text-[10px] text-slate-400">昨 {formatNum(yesterdayVal)}</span>
          </div>
        </div>
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-sm text-white shadow',
            def.accent
          )}
        >
          <i className={cn('fa-solid', def.icon)} aria-hidden />
        </span>
      </div>
    </div>
  );
}

function CollapsibleSection({
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full touch-manipulation items-center justify-between gap-3 px-4 py-3.5 text-left active:bg-slate-50"
        onClick={onToggle}
        aria-expanded={open}
      >
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900">{title}</p>
          {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
        </div>
        <i
          className={cn('fa-solid fa-chevron-down shrink-0 text-xs text-slate-400 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open ? <div className="border-t border-slate-100 px-4 py-3">{children}</div> : null}
    </section>
  );
}

type Props = {
  title?: string;
  subtitle?: string;
  footerNote?: string;
  mpList: AnalyticsMpOption[];
  mpLoading: boolean;
  mpEmptyHint?: string;
  mpId: number;
  onSelectMp: (id: number) => void;
  days: number;
  onDaysChange: (days: number) => void;
  data: AnalyticsResp | null;
  loading: boolean;
  refreshing: boolean;
  err: string;
  onRefresh: () => void;
};

export function MiniprogramAnalyticsMobilePanel({
  title = '访问统计',
  subtitle = '小程序运营数据 · 实时埋点',
  footerNote = 'UV 按 openid 或设备指纹去重；数据每 60 秒自动刷新。',
  mpList,
  mpLoading,
  mpEmptyHint = '暂无可用小程序，请联系管理员配置。',
  mpId,
  onSelectMp,
  days,
  onDaysChange,
  data,
  loading,
  refreshing,
  err,
  onRefresh,
}: Props) {
  const [trendMetricKey, setTrendMetricKey] = useState<MetricKey>(MOBILE_TREND_METRIC_KEYS[0]);
  const [postsOpen, setPostsOpen] = useState(true);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [dailyOpen, setDailyOpen] = useState<string | null>(null);

  const today = data?.today;
  const yesterday = data?.yesterday;
  const daily = data?.daily ?? [];
  const summary = data?.summary;
  const mp = data?.miniprogram;
  const trendMetric = metricByKey(trendMetricKey);

  const primaryMetrics = useMemo(() => METRICS.filter((m) => m.primary), []);
  const secondaryMetrics = useMemo(() => METRICS.filter((m) => !m.primary), []);

  const postSlots = useMemo(() => {
    if (!today || !summary) return [];
    return POST_SLOT_METRICS.map((s) => ({
      ...s,
      todayVal: Math.max(0, Number(today[s.key]) || 0),
      periodVal: Math.max(0, Number(summary[s.key]) || 0),
    }));
  }, [today, summary]);

  return (
    <div className="space-y-4 pb-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
        </div>
        <button
          type="button"
          className="flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm active:bg-slate-50 disabled:opacity-50"
          disabled={refreshing || mpId < 1}
          onClick={onRefresh}
          aria-label="刷新数据"
        >
          <i className={cn('fa-solid fa-arrows-rotate', refreshing && 'animate-spin')} aria-hidden />
        </button>
      </div>

      {mpLoading ? (
        <div className="flex min-h-[4rem] items-center justify-center rounded-2xl border border-slate-200 bg-white">
          <Spinner label="加载小程序" />
        </div>
      ) : mpList.length === 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">{mpEmptyHint}</div>
      ) : (
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <div className="flex w-max gap-2">
            {mpList.map((m) => {
              const active = m.id === mpId;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onSelectMp(m.id)}
                  className={cn(
                    'max-w-[12rem] touch-manipulation rounded-full border px-4 py-2 text-left text-sm font-semibold transition active:scale-[0.98]',
                    active
                      ? 'border-slate-900 bg-slate-900 text-white shadow-md'
                      : 'border-slate-200 bg-white text-slate-700',
                    m.is_slave && !active && 'border-indigo-200 bg-indigo-50/60'
                  )}
                >
                  <span className="block truncate">{m.name}</span>
                  {m.is_slave ? (
                    <span className={cn('block truncate text-[10px] font-normal', active ? 'text-white/80' : 'text-indigo-600')}>
                      从属 · {m.master_name || '主小程序'}
                    </span>
                  ) : null}
                  {m.enabled === false ? (
                    <span className="block text-[10px] font-normal opacity-80">已停用</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {mp ? (
        <p className="text-xs leading-relaxed text-slate-500">
          {data?.server_time ? `更新 ${data.server_time}` : null}
          {data?.tracking_since ? ` · 统计自 ${data.tracking_since}` : ' · 尚无历史，访问后将自动记录'}
        </p>
      ) : null}

      <div className="flex rounded-2xl border border-slate-200 bg-slate-100/80 p-1">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            type="button"
            onClick={() => onDaysChange(opt.days)}
            className={cn(
              'min-h-10 flex-1 touch-manipulation rounded-xl text-sm font-semibold transition',
              days === opt.days ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 active:bg-white/60'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {err ? <p className={inlineErrorClass}>{err}</p> : null}

      {loading && mpId > 0 ? (
        <div className="flex min-h-[280px] items-center justify-center rounded-2xl border border-slate-200 bg-white">
          <Spinner label="加载统计数据" />
        </div>
      ) : mpId > 0 && today && yesterday ? (
        <>
          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">今日概览</h2>
            <div className="grid grid-cols-2 gap-2.5">
              {primaryMetrics.map((def) => (
                <MobileKpiTile
                  key={def.key}
                  def={def}
                  todayVal={Number(today[def.key]) || 0}
                  yesterdayVal={Number(yesterday[def.key]) || 0}
                />
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">互动与转化</h2>
            <div className="grid grid-cols-2 gap-2.5">
              {secondaryMetrics.map((def) => (
                <MobileKpiTile
                  key={def.key}
                  def={def}
                  todayVal={Number(today[def.key]) || 0}
                  yesterdayVal={Number(yesterday[def.key]) || 0}
                />
              ))}
            </div>
          </section>

          {summary ? (
            <section className="rounded-2xl border border-teal-100 bg-gradient-to-br from-white to-teal-50/40 p-3 shadow-sm">
              <p className="text-xs font-bold text-slate-600">{days} 日汇总</p>
              <div className="-mx-1 mt-2 overflow-x-auto px-1 pb-1">
                <div className="flex w-max gap-2">
                  {METRICS.map((def) => (
                    <div
                      key={def.key}
                      className="min-w-[4.5rem] rounded-xl border border-white/80 bg-white/90 px-3 py-2 shadow-sm"
                    >
                      <p className="text-[10px] font-medium text-slate-500">{def.short}</p>
                      <p className="mt-0.5 text-base font-bold tabular-nums text-slate-900">
                        {formatNum(Number(summary[def.key]) || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          <section>
            <div className="-mx-1 mb-2 overflow-x-auto px-1">
              <div className="flex w-max gap-1.5">
                {MOBILE_TREND_METRIC_KEYS.map((key) => {
                  const def = metricByKey(key);
                  const active = key === trendMetricKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTrendMetricKey(key)}
                      className={cn(
                        'touch-manipulation rounded-full px-3 py-1.5 text-xs font-semibold',
                        active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
                      )}
                    >
                      {def.short}
                    </button>
                  );
                })}
              </div>
            </div>
            <AnalyticsTrendChart daily={daily} metric={trendMetric} barAreaPx={MOBILE_CHART_BAR_AREA_PX} compact />
          </section>

          {today && summary ? (
            <CollapsibleSection
              title="信息发布时段"
              subtitle={`今日 ${formatNum(Number(today.posts) || 0)} · ${days} 日 ${formatNum(Number(summary.posts) || 0)}`}
              open={postsOpen}
              onToggle={() => setPostsOpen((v) => !v)}
            >
              {Number(today.posts) > 0 ? (
                <div className="mb-3 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
                  {postSlots.map((s) =>
                    s.todayVal > 0 ? (
                      <div
                        key={s.key}
                        className={cn('h-full', s.accent)}
                        style={{
                          width: `${(s.todayVal / Math.max(Number(today.posts) || 0, 1)) * 100}%`,
                        }}
                      />
                    ) : null
                  )}
                </div>
              ) : (
                <p className="mb-3 text-xs text-slate-400">今日暂无发布</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {postSlots.map((s) => (
                  <div key={s.key} className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className={cn('h-2 w-2 rounded-full', s.accent)} aria-hidden />
                      <span className="text-xs font-semibold text-slate-700">{s.label}</span>
                    </div>
                    <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">{formatNum(s.todayVal)}</p>
                    <p className="text-[10px] text-slate-500">{s.range}</p>
                    <p className="text-[10px] text-slate-400">
                      {days} 日 {formatNum(s.periodVal)}
                    </p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          ) : null}

          {today && summary ? (
            <CollapsibleSection
              title="激励视频明细"
              subtitle={`今日合计 ${formatNum(Number(today.reward_ads) || 0)}`}
              open={rewardOpen}
              onToggle={() => setRewardOpen((v) => !v)}
            >
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2 rounded-xl border border-rose-100 bg-rose-50/50 px-3 py-2.5">
                  <p className="text-[11px] text-slate-500">今日合计</p>
                  <p className="text-xl font-bold tabular-nums text-slate-900">{formatNum(Number(today.reward_ads) || 0)}</p>
                </div>
                {REWARD_AD_SUBMETRICS.map((sub) => (
                  <div key={sub.key} className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5">
                    <p className="text-[11px] text-slate-500">{sub.label}</p>
                    <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                      {formatNum(Number(today[sub.key]) || 0)}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {days} 日 {formatNum(Number(summary[sub.key]) || 0)}
                    </p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          ) : null}

          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">每日明细</h2>
            <div className="space-y-2">
              {[...daily].reverse().map((row) => {
                const isToday = row.ymd === today?.ymd;
                const expanded = dailyOpen === row.ymd;
                return (
                  <div
                    key={row.ymd}
                    className={cn(
                      'overflow-hidden rounded-2xl border bg-white shadow-sm',
                      isToday ? 'border-teal-200 ring-1 ring-teal-100' : 'border-slate-200'
                    )}
                  >
                    <button
                      type="button"
                      className="flex w-full touch-manipulation items-center justify-between gap-2 px-3 py-3 text-left active:bg-slate-50"
                      onClick={() => setDailyOpen(expanded ? null : row.ymd)}
                      aria-expanded={expanded}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="font-semibold tabular-nums text-slate-900">{row.ymd}</span>
                        {isToday ? (
                          <span className="rounded bg-teal-600 px-1.5 py-0.5 text-[10px] font-bold text-white">今日</span>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
                        <span>UV {formatNum(row.uv)}</span>
                        <span>PV {formatNum(row.pv)}</span>
                        <i
                          className={cn('fa-solid fa-chevron-down text-[10px] transition-transform', expanded && 'rotate-180')}
                          aria-hidden
                        />
                      </div>
                    </button>
                    {expanded ? (
                      <div className="border-t border-slate-100 px-3 py-2.5">
                        <div className="grid grid-cols-3 gap-2">
                          {METRICS.map((m) => (
                            <div key={m.key} className="rounded-lg bg-slate-50 px-2 py-1.5">
                              <p className="text-[10px] text-slate-500">{m.short}</p>
                              <p className="text-sm font-bold tabular-nums text-slate-800">
                                {formatNum(Number(row[m.key]) || 0)}
                              </p>
                            </div>
                          ))}
                          {REWARD_AD_SUBMETRICS.map((sub) => (
                            <div key={sub.key} className="rounded-lg bg-rose-50/60 px-2 py-1.5">
                              <p className="text-[10px] text-rose-700/80">激励·{sub.label}</p>
                              <p className="text-sm font-bold tabular-nums text-slate-800">
                                {formatNum(Number(row[sub.key]) || 0)}
                              </p>
                            </div>
                          ))}
                          {POST_SLOT_METRICS.map((sub) => (
                            <div key={sub.key} className="rounded-lg bg-green-50/60 px-2 py-1.5">
                              <p className="text-[10px] text-green-800/80">发布·{sub.label}</p>
                              <p className="text-sm font-bold tabular-nums text-slate-800">
                                {formatNum(Number(row[sub.key]) || 0)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>

          <p className="text-[11px] leading-relaxed text-slate-400">{footerNote}</p>
        </>
      ) : null}
    </div>
  );
}
